import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarDays, CircleCheckBig, CircleX, ClipboardCheck, Download, Eye, MessageSquare, Paperclip, Printer, RefreshCw, RotateCcw, Search, ShieldCheck,
  ShieldX, UserCheck, X, Zap,
} from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import { Select } from '../components/Controls';
import { download, fmtMoney, fmtNumber, toCSV } from '../format';
import { useFrozen } from './attendance/shared';
import { Chip, STATUS_TONE, StageBadge, WARN_TONE, WarnBadge } from './missed/shared';

// Missed Days Review + Approval (Stephanet's MissedDaysReview): approve, assign to leave, decline (salary deduction), request an
// explanation, mark a biometric error or reverse — one record or many. Each decision is recorded on the missed day's audit trail.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = m => (m ? `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}` : '');
// row actions: [action, label, icon, tone, needs a form]
const ROW_ACTIONS = [
  ['approve', 'Approve', CircleCheckBig, 'green', false],
  ['assign_leave', 'Assign Leave', CalendarDays, 'indigo', true],
  ['decline', 'Decline', CircleX, 'red', true],
  ['explain', 'Request Explanation', MessageSquare, 'purple', true],
  ['biometric', 'Biometric Error', Zap, 'teal', false],
  ['reverse', 'Reverse', RotateCcw, 'gray', true],
];
// Supervisor Approval Required: the supervisor's review runs in the approval engine before HR decides
const SUP_ACTIONS = [
  ['authorise', 'Supervisor: Authorised', ShieldCheck, 'green'],
  ['not_authorise', 'Supervisor: Not Authorised', ShieldX, 'red'],
];
const FROZEN = 5;
const BULK = [
  ['approve', 'Approve Selected', CircleCheckBig, 'green'],
  ['assign_leave', 'Assign to Leave', CalendarDays, 'blue'],
  ['decline', 'Decline Selected', CircleX, 'red'],
  ['explain', 'Send for Explanation', MessageSquare, 'outline'],
  ['reverse', 'Reverse Selected', RotateCcw, 'outline'],
];
const balTone = v => (v === null || v === undefined ? '' : v > 0 ? 'mdr-bal-ok' : 'mdr-bal-zero');
const DONE = {
  approve: 'approved — paid without deduction', assign_leave: 'assigned to leave', decline: 'declined — salary deduction raised',
  explain: 'sent to the employee for explanation', biometric: 'marked as biometric error — paid', reverse: 'reversed for review',
  authorise: 'authorised by the supervisor', not_authorise: 'marked not authorised by the supervisor',
};
const bal = v => (v === null || v === undefined ? '—' : fmtNumber(v));

export default function MissedDaysReview() {
  const { notify, canManage } = useHR();
  const [params] = useSearchParams();
  const [f, setF] = useState({ q: params.get('q') || '', status: params.get('status') || '', department: '', month: params.get('month') || '' });
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => hr.mdReview({ status: f.status, department: f.department, month: f.month })
    .then(d => { setData(d); setError(''); setTick(t => t + 1); }).catch(e => setError(e.message)), [f.status, f.department, f.month]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = f.q.trim().toLowerCase();
    return q ? data.rows.filter(r => [r.name, r.employee_no, r.reference].some(v => String(v || '').toLowerCase().includes(q))) : data.rows;
  }, [data, f.q]);
  useEffect(() => { setPicked(p => new Set([...p].filter(id => rows.some(r => r.id === id)))); }, [rows]);
  const tableRef = useFrozen([data, rows.length], FROZEN);

  const run = async (action, list, extra = {}) => {
    setBusy(true);
    try {
      const ids = list.map(r => r.id);
      const res = SUP_ACTIONS.some(([a]) => a === action) ? await hr.mdSupervisorAction({ ids, action, note: extra.note })
        : extra.file ? await hr.mdAssignWithDocument(ids, extra.leave_type, extra.note, extra.file)
          : await hr.mdReviewAction({ ids, action, ...extra });
      if (res.done) notify(`${res.done === 1 ? list.find(r => !res.failed.some(m => m.startsWith(r.reference)))?.reference || '1 record' : `${res.done} records`} ${DONE[action]}`);
      if (res.failed.length) notify(res.failed.slice(0, 2).join(' · ') + (res.failed.length > 2 ? ` (+${res.failed.length - 2} more)` : ''), 'bad');
      setForm(null); setViewing(null); setPicked(new Set());
      await load();
    } catch (err) { notify(err.message, 'bad'); }
    setBusy(false);
  };
  const start = (action, list) => {
    const needsForm = ROW_ACTIONS.find(a => a[0] === action)?.[4] || action === 'not_authorise' || list.length > 1;
    if (needsForm) setForm({ action, rows: list });
    else run(action, list);
  };

  if (error) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>;
  if (!data) return <Loader title="Loading missed days…" subtitle="Fetching missed day records" />;
  const can = data.can.approve && canManage;
  const selected = rows.filter(r => picked.has(r.id));
  const allOn = rows.length > 0 && rows.every(r => picked.has(r.id));
  const toggle = id => setPicked(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const filtered = f.q || f.status || f.department || f.month;
  const totalDeduction = rows.filter(r => r.status === 'Declined - Salary Deduction Required').reduce((s, r) => s + r.deduction, 0);

  const exportCsv = () => download(`missed-days-review${f.month ? `-${f.month}` : ''}.csv`, toCSV([
    { label: 'MD ID', get: r => r.reference }, { label: 'Emp ID', get: r => r.employee_no }, { label: 'Name', get: r => r.name },
    { label: 'Department', get: r => r.department }, { label: 'Date', get: r => r.date }, { label: 'Day', get: r => r.weekday },
    { label: 'Sick Bal', get: r => r.sick }, { label: 'Casual Bal', get: r => r.casual }, { label: 'Annual Bal', get: r => r.annual },
    { label: 'Recommendation', get: r => r.recommendation }, { label: 'Status', get: r => r.status }, { label: 'Warning', get: r => r.warning },
    { label: 'Deduction', get: r => r.deduction || '' }, { label: 'Reviewed By', get: r => r.reviewed_by }, { label: 'Review Note', get: r => r.review_note },
  ], rows));

  return (
    <div className="mdr">
      <div className="mdr-top">
        <div>
          <h2>Missed Days Review + Approval</h2>
          <p>Review, approve, decline, and assign missed days for all employees</p>
        </div>
        <div className="mdr-top-actions no-print">
          <button className="mdr-btn" onClick={() => load().then(() => notify('Missed days refreshed'))}><RefreshCw size={14} /> Refresh</button>
          {data.can.export && <button className="mdr-btn" onClick={exportCsv}><Download size={14} /> Export CSV</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
        </div>
      </div>

      <div className="mdr-card mdr-filters no-print">
        <div className="mdr-search"><Search size={15} /><input placeholder="Search employee name or ID..." value={f.q} onChange={e => setF({ ...f, q: e.target.value })} /></div>
        <Select value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option value="">All Statuses</option>{data.statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Select value={f.department} onChange={e => setF({ ...f, department: e.target.value })}>
          <option value="">All Departments</option>{data.departments.map(d => <option key={d} value={d}>{d}</option>)}
        </Select>
        {f.month && <span className="mdr-month">Month: {monthLabel(f.month)}<button title="Show all months" onClick={() => setF({ ...f, month: '' })}><X size={12} /></button></span>}
        <button className="mdr-btn" onClick={() => setF({ q: '', status: '', department: '', month: '' })}>Clear Filter</button>
      </div>

      {data.rules?.supervisor_approval && (
        <div className="mdr-card mdr-rule no-print">
          <UserCheck size={15} />
          <span><b>Supervisor Approval Required</b> is on: new missed days wait for the employee's supervisor (My Approvals, or the shield buttons here for approvers) before HR can decide them.
            {data.rules.hr_approval ? ' HR then makes the final decision.' : ' HR Approval Required is off, so a day the supervisor authorises is paid without HR.'}</span>
        </div>
      )}

      {can && selected.length > 0 && (
        <div className="mdr-bulk no-print">
          <b>{selected.length} selected</b>
          {BULK.map(([a, label, Ico, tone]) => {
            const ok = selected.filter(r => r.actions.includes(a));
            return <button key={a} className={`mdr-bulk-btn b-${tone}`} disabled={!ok.length || busy} onClick={() => setForm({ action: a, rows: ok })}><Ico size={14} /> {label}{ok.length > 0 && ok.length < selected.length ? ` (${ok.length})` : ''}</button>;
          })}
          {SUP_ACTIONS.map(([a, label, Ico, tone]) => {
            const ok = selected.filter(r => (r.supervisor_actions || []).includes(a));
            return ok.length > 0 && <button key={a} className={`mdr-bulk-btn b-${tone}`} disabled={busy} onClick={() => setForm({ action: a, rows: ok })}><Ico size={14} /> {label}{ok.length < selected.length ? ` (${ok.length})` : ''}</button>;
          })}
          <button className="mdr-btn ghost" onClick={() => setPicked(new Set())}>Clear</button>
        </div>
      )}

      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mdr-frozen" ref={tableRef}>
            <thead>
              <tr>
                <th className="no-print">{can && <input type="checkbox" checked={allOn} disabled={!rows.length} onChange={() => setPicked(allOn ? new Set() : new Set(rows.map(r => r.id)))} aria-label="Select all" />}</th>
                <th className="no-print">Actions</th>
                <th>MD ID</th><th>Emp ID</th><th>Name</th><th>Dept</th><th>Date</th><th>Day</th><th>Sick Bal</th><th>Cas Bal</th><th>Ann Bal</th>
                <th>Recommendation</th><th>Status</th><th>Warning</th><th>Deduction</th>
              </tr>
            </thead>
            <tbody key={tick}>
              {rows.map((r, i) => (
                <tr key={r.id} className={`mdr-row ${picked.has(r.id) ? 'picked' : ''}`} style={{ animationDelay: `${Math.min(i, 20) * 22}ms` }}>
                  <td className="no-print">{can && <input type="checkbox" checked={picked.has(r.id)} onChange={() => toggle(r.id)} aria-label={`Select ${r.reference}`} />}</td>
                  <td className="no-print">
                    <div className="mdr-actions">
                      <button className="mdr-act a-blue" title="View" onClick={() => setViewing(r)}><Eye size={12} /></button>
                      {can && ROW_ACTIONS.filter(([a]) => r.actions.includes(a)).map(([a, label, Ico, tone]) => (
                        <button key={a} className={`mdr-act a-${tone}`} title={label} disabled={busy} onClick={() => start(a, [r])}><Ico size={12} /></button>
                      ))}
                      {can && SUP_ACTIONS.filter(([a]) => (r.supervisor_actions || []).includes(a)).map(([a, label, Ico, tone]) => (
                        <button key={a} className={`mdr-act a-${tone}`} title={label} disabled={busy} onClick={() => start(a, [r])}><Ico size={12} /></button>
                      ))}
                    </div>
                  </td>
                  <td className="mdr-id">{r.reference}</td>
                  <td className="mdr-emp">{r.employee_no}</td>
                  <td className="mdr-name">{r.name}</td>
                  <td>{r.department}</td>
                  <td className="nowrap">{r.date}</td>
                  <td>{r.weekday}</td>
                  <td className={`mdr-bal ${balTone(r.sick)}`}>{bal(r.sick)}</td>
                  <td className={`mdr-bal ${balTone(r.casual)}`}>{bal(r.casual)}</td>
                  <td className={`mdr-bal ${balTone(r.annual)}`}>{bal(r.annual)}</td>
                  <td className="mdr-rec">{r.recommendation ? <span>{r.recommendation}</span> : '—'}</td>
                  <td className="nowrap"><Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip></td>
                  <td><Chip tone={WARN_TONE[r.warning]}>{r.warning}</Chip></td>
                  <td className="mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : <span className="ok">None</span>}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={15} className="mdr-empty">No missed days{filtered ? ' match these filters' : ' to review'}.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="mdr-foot">
          <span>Showing {rows.length} of {data.rows.length} records</span>
          <span>Total Deduction: <strong>{fmtMoney(totalDeduction)}</strong></span>
        </div>
      </div>

      {viewing && <ViewModal r={viewing} can={can} busy={busy} onClose={() => setViewing(null)} onAction={a => start(a, [viewing])} />}
      {form && <ActionModal {...form} leaveTypes={data.leave_types} rules={data.rules || {}} busy={busy} onClose={() => setForm(null)} onSubmit={extra => run(form.action, form.rows, extra)} />}
    </div>
  );
}

function ViewModal({ r, can, busy, onClose, onAction }) {
  const fields = [['Employee ID', r.employee_no], ['Employee Name', r.name], ['Department', r.department], ['Designation', r.designation],
    ['Branch', r.branch], ['Project Site', r.site], ['Date Missed', r.date], ['Day of Week', r.weekday], ['Expected Shift', r.shift],
    ['Biometric Clock-In', r.clock_in || 'NONE'], ['Attendance', r.attendance ? `${r.attendance} (${r.attendance_source || 'manual'})` : 'None'], ['Supervisor', r.supervisor]];
  return (
    <Modal title={`Missed Day Record — ${r.reference}`} onClose={onClose} wide
           footer={<>
             <div className="mdr-view-status"><StageBadge value={r.status} /><WarnBadge value={r.warning} /></div>
             <div className="spacer" />
             {can && [...ROW_ACTIONS.filter(([a]) => r.actions.includes(a)), ...SUP_ACTIONS.filter(([a]) => (r.supervisor_actions || []).includes(a))]
               .map(([a, label, Ico, tone]) => (
                 <button key={a} className={`mdr-act-wide a-${tone}`} disabled={busy} onClick={() => onAction(a)}><Ico size={14} /> {label}</button>
               ))}
             <button className="btn" onClick={onClose}>Close</button>
           </>}>
      <div className="mdr-view">
        <div className="mdr-grid">{fields.map(([l, v]) => <div key={l} className="mdr-field"><span>{l}</span><b>{v || '—'}</b></div>)}</div>
        <h4>Leave Balance</h4>
        <div className="mdr-leave">
          {[['Sick Leave', r.sick, 'teal'], ['Casual Leave', r.casual, 'indigo'], ['Annual Leave', r.annual, 'blue']].map(([l, v, c]) => (
            <div key={l} className={`lc-${c} ${r.recommended_leave && l.startsWith(r.recommended_leave) ? 'rec' : ''}`}><span>{l}</span><b>{bal(v)}</b><small>days available</small></div>
          ))}
        </div>
        <div className="mdr-reco"><ClipboardCheck size={17} /><div><span>System Recommendation</span><b>{r.recommendation || '—'}</b></div></div>
        {r.supervisor_review && (
          <div className="mdr-reco sup"><UserCheck size={17} /><div><span>Supervisor Review</span>
            <b>{r.supervisor_review.verdict}{r.supervisor_review.by ? ` — ${r.supervisor_review.by}` : ''}</b>
            {r.supervisor_review.note && <small>{r.supervisor_review.note}</small>}</div></div>
        )}
        <h4>Payroll Impact</h4>
        <div className="mdr-grid three">
          <div className="mdr-field"><span>Monthly Gross</span><b>{fmtMoney(r.monthly_gross)}</b></div>
          <div className="mdr-field"><span>Daily Rate</span><b>{fmtMoney(r.daily_rate)}</b></div>
          <div className="mdr-field"><span>Deduction</span><b className={r.deduction ? 'md-neg' : ''}>{r.deduction ? fmtMoney(r.deduction) : 'No Deduction'}</b></div>
        </div>
        {(r.reviewed_by || r.review_note || r.reason) && (
          <>
            <h4>History</h4>
            <div className="mdr-grid">
              {r.reason && <div className="mdr-field"><span>Detected</span><b>{r.reason}</b></div>}
              {r.leave_type && <div className="mdr-field"><span>Leave Assigned</span><b>{r.leave_type}</b></div>}
              {r.reviewed_by && <div className="mdr-field"><span>Last Reviewed</span><b>{r.reviewed_by} · {String(r.reviewed_at || '').replace('T', ' ').slice(0, 16)}</b></div>}
              {r.review_note && <div className="mdr-field"><span>Note</span><b>{r.review_note}</b></div>}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

const FORM_TEXT = {
  approve: ['Approve — Paid Without Deduction', 'The missed day is excused and paid in full.', 'Approve'],
  assign_leave: ['Assign to Leave', 'An approved one-day leave is booked for the missed date and charged to the leave balance.', 'Assign Leave'],
  decline: ['Decline — Salary Deduction', 'The day is deducted from salary in the payroll month of the missed date.', 'Decline & Deduct'],
  explain: ['Request Explanation', 'The record moves to "Sent to Employee" until HR reviews the explanation.', 'Send for Explanation'],
  biometric: ['Biometric Error — Paid', 'The absence is treated as a device fault and paid in full.', 'Confirm'],
  reverse: ['Reverse Decision', 'The decision is undone and the record returns to review. A leave booked for it is cancelled and a deduction withdrawn.', 'Reverse'],
  authorise: ['Supervisor Review — Authorised', 'The supervisor confirms the absence was authorised. You act on the review step as its approver.', 'Authorise'],
  not_authorise: ['Supervisor Review — Not Authorised', 'The supervisor did not authorise the absence; HR decides the day next.', 'Not Authorised'],
};
function ActionModal({ action, rows, leaveTypes, rules, busy, onClose, onSubmit }) {
  const one = rows.length === 1 ? rows[0] : null;
  const [leaveType, setLeaveType] = useState(one?.recommended_leave || '');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [title, intro, cta] = FORM_TEXT[action];
  const needsNote = ['explain', 'reverse', 'not_authorise'].includes(action);
  // Require Attachment for Sick Leave / Medical Report After 1 Sick Day
  const sickDoc = action === 'assign_leave' && leaveType === 'Sick' && (rules.sick_attachment || rows.some(r => r.sick_needs_document));
  const deduction = rows.reduce((s, r) => s + r.daily_rate * (r.days || 1), 0);
  const balOf = (r, t) => ({ Sick: r.sick, Casual: r.casual, Annual: r.annual }[t]);
  const submit = e => {
    e.preventDefault();
    onSubmit({ note: note || undefined, ...(action === 'assign_leave' && leaveType ? { leave_type: leaveType } : {}), ...(sickDoc && file ? { file } : {}) });
  };
  return (
    <Modal title={one ? `${title} — ${one.reference}` : `${title} (${rows.length} records)`} onClose={onClose}>
      <form className="mdr-form" onSubmit={submit}>
        <p className="muted small">{intro}</p>
        <div className="mdr-who">
          {rows.slice(0, 6).map(r => <span key={r.id}><b>{r.name}</b> {r.reference} · {r.date}</span>)}
          {rows.length > 6 && <span className="muted">+{rows.length - 6} more</span>}
        </div>
        {action === 'assign_leave' && (
          <div className="field">
            <span className="field-label">Leave Type {!one && <em className="muted">(empty = each record's recommended leave)</em>}</span>
            <div className="mdr-leave-pick">
              {!one && <button type="button" className={leaveType === '' ? 'on' : ''} onClick={() => setLeaveType('')}>Recommended</button>}
              {leaveTypes.map(t => {
                const b = one ? balOf(one, t) : undefined;
                const none = one && b !== undefined && b !== null && b < (one.days || 1);
                return <button key={t} type="button" disabled={none} className={leaveType === t ? 'on' : ''} onClick={() => setLeaveType(t)}>
                  {t}{b !== undefined && b !== null ? <small>{fmtNumber(b)} left</small> : null}
                </button>;
              })}
            </div>
          </div>
        )}
        {sickDoc && (
          <div className="field">
            <span className="field-label"><Paperclip size={12} /> Medical Document<em>*</em></span>
            <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={e => setFile(e.target.files[0] || null)} />
            <small className="muted">{rules.sick_attachment ? 'Require Attachment for Sick Leave is on.' : 'This makes more than one consecutive sick day — a medical report is required.'} PDF, PNG or JPEG; attached to the booked leave.</small>
          </div>
        )}
        {action === 'decline' && (
          <div className="mdr-impact"><span>Salary deduction</span><b className="md-neg">{fmtMoney(deduction)}</b>
            <small>{one ? `${fmtNumber(one.days || 1)} day × ${fmtMoney(one.daily_rate)} daily rate` : `${rows.length} records at each employee's daily rate`}</small></div>
        )}
        <div className="field">
          <span className="field-label">{needsNote ? 'Reason' : 'Note'}{needsNote ? <em>*</em> : <em className="muted"> (optional)</em>}</span>
          <textarea rows={3} value={note} required={needsNote} placeholder={action === 'explain' ? 'What should the employee explain?' : 'Add a note for the audit trail'} onChange={e => setNote(e.target.value)} />
        </div>
        <div className="form-actions">
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className={`btn ${action === 'decline' ? 'danger' : 'primary'}`} disabled={busy || (action === 'assign_leave' && one && !leaveType) || (needsNote && !note.trim()) || (sickDoc && !file)}>
            {busy ? 'Saving…' : cta}
          </button>
        </div>
      </form>
    </Modal>
  );
}
